import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReceiptCostRow, ItemSummary, QueryResponse } from '../../../services/d314Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtPrice = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0.000';
  return v.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const detailColumns: ColumnsType<ReceiptCostRow> = [
  {
    title: '產品代號',
    dataIndex: 'itemCode',
    key: 'itemCode',
    width: 130,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '完成品名',
    dataIndex: 'finishName',
    key: 'finishName',
    width: 80,
    render: (v: string) => (v || '').trim() || '-',
  },
  {
    title: '進貨單號',
    dataIndex: 'receiptNo',
    key: 'receiptNo',
    width: 100,
  },
  {
    title: '入庫日期',
    dataIndex: 'receiptDt',
    key: 'receiptDt',
    width: 100,
  },
  {
    title: '供應商',
    dataIndex: 'supplier',
    key: 'supplier',
    width: 120,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '數量',
    dataIndex: 'qty',
    key: 'qty',
    width: 80,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '單位',
    dataIndex: 'unit',
    key: 'unit',
    width: 50,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '單價',
    dataIndex: 'price',
    key: 'price',
    width: 90,
    align: 'right' as const,
    render: (v: number) => fmtPrice(v),
  },
  {
    title: '幣別',
    dataIndex: 'currency',
    key: 'currency',
    width: 50,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '金額',
    dataIndex: 'amount',
    key: 'amount',
    width: 100,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '來源',
    dataIndex: 'source',
    key: 'source',
    width: 80,
    render: (v: string) => {
      const label = v === 'purchase' ? '採購' : '委外';
      const color = v === 'purchase' ? 'blue' : 'orange';
      return <Tag color={color}>{label}</Tag>;
    },
  },
];

const summaryColumns: ColumnsType<ItemSummary> = [
  {
    title: '產品代號',
    dataIndex: 'itemCode',
    key: 'itemCode',
    width: 130,
  },
  {
    title: '總數量',
    dataIndex: 'totalQty',
    key: 'totalQty',
    width: 100,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '總金額',
    dataIndex: 'totalAmt',
    key: 'totalAmt',
    width: 120,
    align: 'right' as const,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '筆數',
    dataIndex: 'rowCount',
    key: 'rowCount',
    width: 60,
    align: 'center' as const,
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.receiptNo}-${row.seqNo}-${i}`,
      })),
    [result.details]
  );

  const summaryData = useMemo(
    () =>
      (result.summaries || []).map((row, i) => ({
        ...row,
        _key: `${row.itemCode}-${i}`,
      })),
    [result.summaries]
  );

  const items = [
    {
      key: 'details',
      label: `進貨明細 (${result.totalCount})`,
      children: (
        <Table
          columns={detailColumns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 1100, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
    {
      key: 'summary',
      label: `產品彙總 (${summaryData.length})`,
      children: (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey="_key"
          size="small"
          scroll={{ x: 500, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
